import { QuestionCircleTwoTone } from '@ant-design/icons';
import { Image, Space, Tag, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { FilterCriteriaItem, FilterOperatorEnum } from 'domains/appDomain';
import { isRegularOrg } from 'domains/orgDomain';
import {
  EMPTY_BASE_SPEC,
  Svc,
  SvcSpec,
  SvcType,
  getUomLabel,
} from 'domains/svcDomain';
import { useOrg } from 'hooks/useOrg';
import { cloneDeep, isEmpty, isUndefined, xor } from 'lodash-es';
import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPLOAD_FILE_ADDRESS_PREFIX } from 'utils/constants';

export const SVCTable = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const org = useOrg();
  const regular = org && isRegularOrg(org);

  const loading = useAppSelector(
    (state) => state.loading.effects.svc.getAllSVCs
  );

  const curItem = useAppSelector((state) => state.svc.curItem);

  const formVisible = useAppSelector(
    (state) =>
      state.svc.creatingSvc ||
      state.svc.creatingSvcSpec ||
      state.svc.viewingSvc ||
      state.svc.viewingSvcSpec
  );

  const data = useAppSelector((state) => state.svc.data);

  const filterObj = useAppSelector((state) => state.svc.filterObj);
  const filterSvcFunc: (svc: Svc) => boolean = useMemo(() => {
    return (svc: Svc) => {
      console.log('filtering', filterObj?.svcTypeId);
      if (
        isUndefined(filterObj.svcRevxId) &&
        isUndefined(filterObj.description) &&
        isEmpty(svc.svcSpecs)
      ) {
        return false;
      }
      if (!isUndefined(filterObj.svcRevxId)) {
        const revxIdValue = filterObj.svcRevxId.values[0];
        if (svc.revxId !== revxIdValue) {
          return false;
        }
      }
      if (!isUndefined(filterObj.svcTypeId)) {
        const svcTypeIdValues = filterObj.svcTypeId.values as number[];
        if (!svcTypeIdValues.includes(svc.svcType.id)) {
          return false;
        }
      }
      if (!isUndefined(filterObj.uom)) {
        const uomValues = filterObj.uom.values;
        if (!uomValues.includes(svc.uom)) {
          return false;
        }
      }
      if (!isUndefined(filterObj.uomCoefficient)) {
        const uomCoefficientValues = filterObj.uomCoefficient.values;
        if (!uomCoefficientValues.includes(svc.uomCoefficient)) {
          return false;
        }
      }
      if (!isEmpty(svc.svcSpecs)) {
        // specs got filtered first
        // any non-empty svc should be kept
        return true;
      }
      if (!isUndefined(filterObj.description)) {
        const descValue = (
          filterObj.description.values[0] as string
        ).toLocaleLowerCase();
        if (!svc.name.toLocaleLowerCase().includes(descValue)) {
          return false;
        }
      }

      return true;
    };
  }, [filterObj]);

  const filterSpecFunc: (spec: SvcSpec) => boolean = useMemo(() => {
    return (spec: SvcSpec) => {
      if (!isUndefined(filterObj.specRevxId)) {
        const revxIdValue = filterObj.specRevxId.values[0];
        if (spec.revxId !== revxIdValue) {
          return false;
        }
      }
      if (!isUndefined(filterObj.description)) {
        const descValue = (
          filterObj.description.values[0] as string
        ).toLocaleLowerCase();
        if (!spec.description.toLocaleLowerCase().includes(descValue)) {
          return false;
        }
      }
      if (!isUndefined(filterObj.makereadyCostUsd)) {
        if (
          !isValueMatchCriteria(
            spec.makereadyCostUsd,
            filterObj.makereadyCostUsd
          )
        ) {
          return false;
        }
      }
      if (!isUndefined(filterObj.makereadyTimeHr)) {
        if (
          !isValueMatchCriteria(spec.makereadyTimeHr, filterObj.makereadyTimeHr)
        ) {
          return false;
        }
      }
      if (!isUndefined(filterObj.markupPct)) {
        if (!isValueMatchCriteria(spec.markupPct, filterObj.markupPct)) {
          return false;
        }
      }
      if (!isUndefined(filterObj.materialCostUsd)) {
        if (
          !isValueMatchCriteria(spec.materialCostUsd, filterObj.materialCostUsd)
        ) {
          return false;
        }
      }
      if (!isUndefined(filterObj.materialMarkupPct)) {
        if (
          !isValueMatchCriteria(
            spec.materialMarkupPct,
            filterObj.materialMarkupPct
          )
        ) {
          return false;
        }
      }
      if (!isUndefined(filterObj.timeHr)) {
        if (!isValueMatchCriteria(spec.timeHr, filterObj.timeHr)) {
          return false;
        }
      }
      if (!isUndefined(filterObj.costUsd)) {
        if (!isValueMatchCriteria(spec.costUsd, filterObj.costUsd)) {
          return false;
        }
      }
      return true;
    };
  }, [filterObj]);

  const currentPage = useAppSelector((state) => state.svc.currentPage);
  const pageSize = useAppSelector((state) => state.svc.pageSize);
  const [expandedRows, setExpandedRows] = useState<Key[]>([]);

  const filteredData = useMemo(() => {
    if (isUndefined(filterObj) || isEmpty(filterObj) || isUndefined(data)) {
      return data;
    }
    const clonedData = cloneDeep(data);
    clonedData.forEach(
      (svc) => (svc.svcSpecs = (svc.svcSpecs || []).filter(filterSpecFunc))
    );
    const result = clonedData.filter(filterSvcFunc);
    const expandedRowKeys = result
      .filter((svc) => !isEmpty(svc.svcSpecs))
      .map((svc) => 'svc' + svc.id);
    setExpandedRows(expandedRowKeys);
    return result;
  }, [filterObj, data, filterSvcFunc, filterSpecFunc]);

  const pagedData = useMemo(() => {
    return (
      filteredData?.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ) || []
    );
  }, [currentPage, filteredData, pageSize]);

  const svcColomns: TableColumnsType<Svc> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      width: 80,
    },
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
      width: 80,
    },
    {
      title: t('platform.svc.tableColumn.type'),
      dataIndex: 'svcType',
      width: 120,
      render: (svcType: SvcType) => {
        return svcType?.name;
      },
    },
    {
      title: t('platform.svc.tableColumn.name'),
      width: 120,
      dataIndex: 'name',
    },
    {
      title: t('platform.svc.form.image'),
      dataIndex: 'image',
      width: 40,
      render: (value) =>
        isEmpty(value) ? (
          <></>
        ) : (
          <Image width={24} src={UPLOAD_FILE_ADDRESS_PREFIX + value} />
        ),
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.uom')}
          <Tooltip title={t('platform.svc.tooltip.uom')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      dataIndex: 'uom',
      render: (text) => {
        return getUomLabel(text);
      },
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.uomCoefficient')}
          <Tooltip title={t('platform.svc.tooltip.uomCoefficient')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      responsive: ['xxl'],
      dataIndex: 'uomCoefficient',
      render: (val) => {
        return (val || '').split('_').join(' ');
      },
    },
    {
      title: t('common.status'),
      dataIndex: 'active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
  ];
  const specColumns: TableColumnsType<SvcSpec> = [
    {
      title: 'Spec',
      width: 40,
    },
    {
      title: t('common.id'),
      dataIndex: 'id',
      width: 80,
    },
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
      width: 80,
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      width: 80,
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.costUsd')}
          <Tooltip title={t('platform.svc.tooltip.costUsd')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      dataIndex: 'costUsd',
      valueType: 'money',
      decimalDigits: 4,
      responsive: ['xl'],
    },
    {
      title: (
        <Space>
          {t('platform.svc.tableColumn.makereadyCost')}
          <Tooltip title={t('platform.svc.tooltip.makereadyCost')}>
            <QuestionCircleTwoTone />
          </Tooltip>
        </Space>
      ),
      width: 120,
      dataIndex: 'makereadyCostUsd',
      valueType: 'money',
      decimalDigits: 2,
      responsive: ['xl'],
    },
    {
      title: t('platform.svc.tableColumn.markupPct'),
      width: 80,
      dataIndex: 'markupPct',
      valueType: 'percentage',
      responsive: ['xl'],
    },

    {
      title: t('platform.svc.tableColumn.serviceMaterialMarkup'),
      dataIndex: 'materialMarkupPct',
      valueType: 'percentage',
      width: 100,
      responsive: ['xl'],
    },
    {
      title: t('common.status'),
      dataIndex: 'active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
  ];

  const svcActionColumns: TableColumnsType<Svc> = [
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => {
              dispatch.svc.setState({ viewingSvc: true, curItem: record });
            }}
          >
            {t('common.view')}
          </RevxButton>
          <RevxButton
            type="link"
            key="addspec"
            size="small"
            onClick={() => {
              if (regular) {
                dispatch.svc.setState({
                  specBaseSelectState: {
                    visible: true,
                    orgSvc: record,
                  },
                });
              } else {
                dispatch.svc.setState({
                  curItem: record,
                  creatingSvcSpec: true,
                  curSpec: {
                    ...EMPTY_BASE_SPEC,
                    svcId: record.id,
                    parameters:
                      record.parameterLabels === null
                        ? []
                        : record.parameterLabels.map((pL) => ({
                            label: pL,
                            parameter: '',
                          })),
                  },
                });
              }
            }}
          >
            {t('platform.svc.createBaseSpec')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const specActionColumns: TableColumnsType<SvcSpec> = [
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => {
              dispatch.svc.setState({ viewingSvcSpec: true, curSpec: record });
            }}
          >
            {t('common.view')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: Svc) => {
    return (
      <RevxTable<SvcSpec>
        size="small"
        pagination={false}
        columns={[...specColumns, ...specActionColumns]}
        scroll={undefined}
        dataSource={record.svcSpecs}
        rowKey={(record) => 'spec' + record.id}
      />
    );
  };

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pageSize) {
            dispatch.svc.setState({
              pageSize: pagination.pageSize,
              currentPage: 1,
            });
          }

          if (pagination.current !== currentPage) {
            dispatch.svc.setState({
              currentPage: pagination.current,
            });
          }
          break;
      }
    },
    [currentPage, dispatch.svc, pageSize]
  );

  useEffect(() => {
    dispatch.svc.getAllSVCs();
  }, [dispatch.svc]);

  return (
    <div hidden={formVisible}>
      <RevxTable<Svc>
        loading={loading}
        size="small"
        columns={[...svcColomns, ...svcActionColumns]}
        pagination={{
          pageSize: pageSize,
          total: filteredData?.length,
          current: currentPage,
          showTotal: (total) => t('common.pagerTotal', { total: total }),
        }}
        rowClassName={(record: Svc) => {
          if (curItem?.id === record.id) {
            return 'editing';
          }
          return '';
        }}
        indentSize={20}
        scroll={undefined}
        dataSource={pagedData || []}
        onChange={onTableChange}
        expandable={{
          expandRowByClick: false,
          onExpand: (expanded, record) => {
            setExpandedRows(xor(expandedRows, ['svc' + record.id]));
          },
          expandedRowKeys: expandedRows,
          expandedRowRender: expandedRowRender,
        }}
        rowKey={(record) => 'svc' + record.id}
      />
    </div>
  );
};

function isValueMatchCriteria(
  value: number,
  criteria: FilterCriteriaItem
): boolean {
  const criteriaValue = criteria.values[0] as number;
  switch (criteria.operator) {
    case FilterOperatorEnum.EQUALS:
      return value === criteriaValue;
    case FilterOperatorEnum.GREATER_THAN:
      return value > criteriaValue;
    case FilterOperatorEnum.LESS_THAN:
      return value < criteriaValue;
    case FilterOperatorEnum.EXISTS:
      return value !== null;
    case FilterOperatorEnum.NOT_EXISTS:
      return value === null;
    default:
      return true;
  }
}
